import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { renderThrough, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The loud announcement: a full-width band above everything, in the colour of
 * whatever it is announcing, with a way to make it go away.
 *
 *   <AlertBanner>
 *     <AlertBannerMessage render={<Link href={link} />}>
 *       {icon}
 *       <AlertBannerBody>
 *         <AlertBannerText variant={AlertBannerTextVariant.UNDERLINED}>
 *           <span>{title}: </span>
 *           <span>{message}</span>
 *         </AlertBannerText>
 *         <AlertBannerBadge variant={AlertBannerBadgeVariant.WARNING}>
 *           <AlertBannerBadgeLabel>{buttonText}</AlertBannerBadgeLabel>
 *           <ChevronRight />
 *         </AlertBannerBadge>
 *       </AlertBannerBody>
 *     </AlertBannerMessage>
 *
 *     <AlertBannerDismiss>{closeControl}</AlertBannerDismiss>
 *   </AlertBanner>
 *
 * **The band's ground is painted on the tenant's own wrapper, from this
 * section's recipe.** Which colour an announcement wears is a fact about what
 * the announcement IS, so the map from one to the other is the section's
 * (`alertBannerGroundVariants`) even though the element it lands on is the
 * full-bleed wrapper a tenant already has. The band inherits its ink from
 * there; the only thing in here that has to name a colour of its own is the
 * chip's ring, which cannot inherit one.
 *
 * **The dismissal is a slot, not a control.** What the button does — which
 * message it forgets, and for how long — is the shop's; the banner only says
 * where it sits and that it stays at the top of a message that wrapped.
 *
 * Distinct from `alertMessage`, which is the thin bar's row. They differ in
 * which parts exist (this one is dismissible), in type scale, in spacing and in
 * how the chip is drawn — more than an appearance apart, so two sections.
 */

/**
 * The colour the whole band wears, on the same axis as the chip's ring.
 *
 * A recipe rather than a part: the element the ground is painted on is the
 * full-bleed wrapper a tenant already has around the banner, so what crosses is
 * the map from what an announcement IS to what it looks like — which is the
 * thing every tenant was otherwise going to write out again beside its own
 * copy of the badge map.
 */
export const alertBannerGroundVariants = cva('', {
  variants: {
    variant: {
      /** Nothing announced in particular: the page's own ground. */
      base: '',
      /** Something to be careful about. */
      warning: 'bg-warning text-warning-foreground',
      /** Something went wrong. */
      destructive: 'bg-destructive text-destructive-foreground',
      /** Something went right. */
      success: 'bg-success text-success-foreground',
    },
  },
});

type AlertBannerGroundVariantName = NonNullable<
  VariantProps<typeof alertBannerGroundVariants>['variant']
>;

export type AlertBannerGroundVariant = AlertBannerGroundVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const AlertBannerGroundVariant = {
  /** The page's own ground. */
  BASE: 'base',
  /** Something to be careful about. */
  WARNING: 'warning',
  /** Something went wrong. */
  DESTRUCTIVE: 'destructive',
  /** Something went right. */
  SUCCESS: 'success',
} as const satisfies Record<string, AlertBannerGroundVariantName>;

/** The band's row: the message, then whatever closes it, pushed apart. */
const alertBannerClasses = 'lap:gap-6 flex flex-1 items-center justify-between gap-3 py-2';

export type AlertBannerProps = ComponentProps<'div'>;

/** The banner row. */
export const AlertBanner = ({ className, ...props }: AlertBannerProps) => (
  <div data-slot="alert-banner" className={clsxm(alertBannerClasses, className)} {...props} />
);

/**
 * The message itself — mark, copy and chip — and the click surface where the
 * announcement goes somewhere. Neutral element where it does not, for the same
 * reason `alertMessage` has one: an empty `href` is a reload.
 */
const alertBannerMessageClasses =
  'group lap:[&>svg]:size-5 flex gap-2 [&>svg]:size-4.5 [&>svg]:shrink-0';

export type AlertBannerMessageProps = ComponentProps<'div'> & {
  /** The element to paint instead of the neutral `<div>` — an anchor, where the message goes somewhere. */
  readonly render?: RenderElement;
};

/** The message half of the banner. */
export const AlertBannerMessage = ({ className, render, ...props }: AlertBannerMessageProps) => {
  const resolved = clsxm(alertBannerMessageClasses, className);

  return (
    renderThrough(render, 'alert-banner-message', resolved, props) ?? (
      <div data-slot="alert-banner-message" className={resolved} {...props} />
    )
  );
};

/** The copy and the chip beside it, wrapping onto a second line where narrow. */
const alertBannerBodyClasses = 'flex flex-wrap gap-2';

export type AlertBannerBodyProps = ComponentProps<'div'>;

/** Everything in the message except the mark. */
export const AlertBannerBody = ({ className, ...props }: AlertBannerBodyProps) => (
  <div
    data-slot="alert-banner-body"
    className={clsxm(alertBannerBodyClasses, className)}
    {...props}
  />
);

/**
 * The copy. Heavier and a step larger than the thin bar's, and it wraps rather
 * than clamping — a banner is allowed to be two lines tall.
 */
export const alertBannerTextVariants = cva(
  'lap:text-[15px]/5 text-sm/4.5 font-semibold tracking-tight',
  {
    variants: {
      variant: {
        /** Underlines with the message under a pointer — where it goes somewhere. */
        underlined: 'group-hover:underline group-hover:underline-offset-2',
        /** Reads as copy and stays that way. */
        plain: '',
      },
    },
  }
);

type AlertBannerTextVariantName = NonNullable<
  VariantProps<typeof alertBannerTextVariants>['variant']
>;

export type AlertBannerTextVariant = AlertBannerTextVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const AlertBannerTextVariant = {
  /** Underlines with the message. */
  UNDERLINED: 'underlined',
  /** Stays copy. */
  PLAIN: 'plain',
} as const satisfies Record<string, AlertBannerTextVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type AlertBannerTextVariantProps = { readonly variant: AlertBannerTextVariant };

export type AlertBannerTextProps = ComponentProps<'div'> & AlertBannerTextVariantProps;

/** The banner copy. */
export const AlertBannerText = ({ className, variant, ...props }: AlertBannerTextProps) => (
  <div
    data-slot="alert-banner-text"
    className={clsxm(alertBannerTextVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The chip at the end of the copy.
 *
 * Its ring is the one thing in the banner that cannot inherit the band's ink,
 * so the appearance names it — which is why this part carries an axis and the
 * rest of the banner does not.
 */
export const alertBannerBadgeVariants = cva(
  'lap:[&>svg]:size-4 flex items-center rounded-sm px-1 font-normal whitespace-nowrap ring [&>svg]:size-3 [&>svg]:shrink-0 [&>svg]:opacity-50',
  {
    variants: {
      variant: {
        /** On the cautionary ground. */
        warning: 'ring-warning-foreground/20',
        /** On the ground that says something went wrong. */
        destructive: 'ring-destructive-foreground/20',
        /** On the ground that says something went right. */
        success: 'ring-success-foreground/20',
        /** The opt-out: a ground this section has no colour for, ringed in the inherited ink. */
        unstyled: '',
      },
    },
  }
);

type AlertBannerBadgeVariantName = NonNullable<
  VariantProps<typeof alertBannerBadgeVariants>['variant']
>;

export type AlertBannerBadgeVariant = AlertBannerBadgeVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const AlertBannerBadgeVariant = {
  /** On the cautionary ground. */
  WARNING: 'warning',
  /** On the ground that says something went wrong. */
  DESTRUCTIVE: 'destructive',
  /** On the ground that says something went right. */
  SUCCESS: 'success',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, AlertBannerBadgeVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type AlertBannerBadgeVariantProps = { readonly variant: AlertBannerBadgeVariant };

export type AlertBannerBadgeProps = ComponentProps<'div'> & AlertBannerBadgeVariantProps;

/** The chip at the end of the copy. */
export const AlertBannerBadge = ({ className, variant, ...props }: AlertBannerBadgeProps) => (
  <div
    data-slot="alert-banner-badge"
    className={clsxm(alertBannerBadgeVariants({ variant }), className)}
    {...props}
  />
);

/** The chip's label, reading at the copy's size. */
const alertBannerBadgeLabelClasses = 'lap:text-[15px]/5 text-sm/4.5';

export type AlertBannerBadgeLabelProps = ComponentProps<'span'>;

/** The chip's label. */
export const AlertBannerBadgeLabel = ({ className, ...props }: AlertBannerBadgeLabelProps) => (
  <span
    data-slot="alert-banner-badge-label"
    className={clsxm(alertBannerBadgeLabelClasses, className)}
    {...props}
  />
);

/**
 * Where the way out sits: at the trailing end, and at the TOP of it — a message
 * that wrapped to three lines must not push the close control to the middle of
 * them. Its own place, in its own recipe, rather than an alignment the row
 * happens to give it.
 *
 * CAUTION: no negative margin here, and no other class that moves this slot
 * toward the band's trailing edge. The close control is an icon wrapped in a
 * 44px touch target (`@vs-shop/ui/touchTarget`), which is absolutely positioned
 * and centred on an icon roughly 18px wide — so it reaches about 13px past the
 * control on each side, and the page gutter below `lap` is 16px. Nothing in
 * this repo clips horizontal overflow, so a touch target that crosses the
 * viewport edge makes the WHOLE DOCUMENT horizontally scrollable, and iOS then
 * rubber-bands the page sideways on any horizontal drag. One pixel is enough.
 * Pinned by `alertBanner.browser.test.tsx`.
 */
const alertBannerDismissClasses = 'flex self-start';

export type AlertBannerDismissProps = ComponentProps<'div'>;

/** Where the dismiss control sits. The control itself is the tenant's. */
export const AlertBannerDismiss = ({ className, ...props }: AlertBannerDismissProps) => (
  <div
    data-slot="alert-banner-dismiss"
    className={clsxm(alertBannerDismissClasses, className)}
    {...props}
  />
);
